import type { Business } from '../types';
import {
  LIBRARY_STATUS_LABELS,
  PRIORITY_LABELS,
  PROSPECT_STATUS_LABELS,
  WEBSITE_TYPE_LABELS,
} from './labels';

export const CSV_HEADERS = [
  'name',
  'category',
  'address',
  'distance_km',
  'phone',
  'website',
  'website_type',
  'rating',
  'review_count',
  'prospect_score',
  'prospect_priority',
  'library_status',
  'prospect_status',
  'google_maps_url',
  'place_id',
] as const;

/**
 * Separateur point-virgule + decimale a la virgule : c'est le format CSV
 * attendu par Excel et LibreOffice en configuration francaise. Combine au BOM
 * UTF-8 ajoute au telechargement, les accents s'affichent correctement.
 */
const DELIMITER = ';';
const NEWLINE = '\r\n';
const BOM = '\uFEFF';

function escapeField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';

  const text = typeof value === 'number' ? String(value) : value;
  const needsQuotes =
    text.includes(DELIMITER) ||
    text.includes('"') ||
    text.includes('\n') ||
    text.includes('\r') ||
    text !== text.trim();

  if (!needsQuotes) return text;
  // Une guillemet interne est doublee (RFC 4180).
  return `"${text.replace(/"/g, '""')}"`;
}

function formatDecimal(value: number, digits: number): string {
  return value.toFixed(digits).replace('.', ',');
}

/** Export trie par score decroissant, quel que soit le tri affiche a l'ecran. */
export function buildCsv(businesses: readonly Business[]): string {
  const sorted = [...businesses].sort(
    (a, b) => b.prospectScore - a.prospectScore || a.distanceMeters - b.distanceMeters,
  );

  const rows = sorted.map((business) =>
    [
      escapeField(business.name),
      escapeField(business.category),
      escapeField(business.address),
      escapeField(formatDecimal(business.distanceKm, 2)),
      escapeField(business.phone),
      escapeField(business.website),
      escapeField(WEBSITE_TYPE_LABELS[business.websiteType]),
      escapeField(business.rating === null ? '' : formatDecimal(business.rating, 1)),
      escapeField(business.userRatingCount),
      escapeField(business.prospectScore),
      escapeField(PRIORITY_LABELS[business.prospectPriority]),
      escapeField(LIBRARY_STATUS_LABELS[business.libraryStatus]),
      escapeField(
        business.prospectStatus === null ? '' : PROSPECT_STATUS_LABELS[business.prospectStatus],
      ),
      escapeField(business.googleMapsUrl),
      escapeField(business.placeId),
    ].join(DELIMITER),
  );

  return [CSV_HEADERS.join(DELIMITER), ...rows].join(NEWLINE) + NEWLINE;
}

export function slugify(value: string): string {
  return (
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'prospects'
  );
}

export function buildFileName(query: string, address: string): string {
  const date = new Date().toISOString().slice(0, 10);
  const activity = query.trim().length === 0 ? 'toutes-categories' : slugify(query);
  return `prospects-${activity}-${slugify(address)}-${date}.csv`;
}

/** Declenche le telechargement du CSV (BOM inclus pour Excel). */
export function downloadCsv(businesses: readonly Business[], fileName: string): void {
  const blob = new Blob([BOM + buildCsv(businesses)], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
